import { yupResolver } from '@hookform/resolvers/yup';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Dialog, DialogActions, DialogContent, Grid } from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { ControlledTextInput } from '../../../../../components/basics/ControlledTextInput';
import { DialogHeader } from '../../../../../components/basics/DialogHeader';
import { PasswordStrength, PasswordStrengthComponent } from '../../../../../components/basics/PasswordStrengthComponent';
import { useAuthContext } from '../../../../../context/AuthContextProvider';
import { AuthService } from '../../../../../services/Auth.service';
import { hideSpinner, showSpinner } from '../../../../../store/slicers/globalSpinner.slicer';
import { showSnackbarAlert } from '../../../../../store/slicers/snackbarAlert.slicer';
import { UserPasswordEditSchema } from '../../../../../utils/forms/validations/formValidations';
import { strengthColor, strengthIndicator } from '../../../../../utils/password-strength';

interface FormFields {
  current_password: string;
  new_password: string;
  password_confirm: string;
}

type Props = {
  showModal: boolean;
  hideModal: () => void;
};

export const EditPasswordModal = ({ showModal, hideModal }: Props) => {
  const dispatch = useDispatch();
  const { user } = useAuthContext();
  const [level, setLevel] = useState<PasswordStrength | null>(null);

  const {
    control,
    reset,
    handleSubmit,
    getValues,
    setError,
    formState: { errors, isDirty, isValid }
  } = useForm<FormFields>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(UserPasswordEditSchema)
  });

  const passwordWatch = useWatch({
    control,
    name: 'new_password',
    defaultValue: ''
  });

  const onSubmit = async (data: FormFields) => {
    if (level && level.level < 2) {
      dispatch(
        showSnackbarAlert({
          title: 'Erro',
          message: 'A força da senha deve ser no mínimo "boa". Por favor, insira uma senha mais forte.',
          severity: 'error'
        })
      );
      return;
    }

    dispatch(showSpinner());
    const submitData = {
      senha_atual: data.current_password,
      senha: data.new_password,
      confirmacao: data.password_confirm,
      usuario: String(user?.usuario)
    };
    try {
      await AuthService.changePassword(submitData);
      dispatch(
        showSnackbarAlert({
          title: 'Sucesso',
          message: 'Senha atualizada com sucesso!',
          severity: 'success'
        })
      );
      reset({});
      hideModal();
    } catch (error: any) {
      dispatch(
        showSnackbarAlert({
          title: 'Erro',
          message: 'Ocorreu um erro ao tentar alterar a senha: ' + error?.data?.error?.message || error,
          severity: 'error'
        })
      );
    } finally {
      dispatch(hideSpinner());
    }
  };

  const changePassword = (value: any) => {
    const temp = strengthIndicator(value);
    setLevel(strengthColor(temp));
  };

  useEffect(() => {
    if (passwordWatch) {
      changePassword(passwordWatch);
    }
  }, [passwordWatch]);

  return (
    <Dialog open={showModal} onClose={hideModal}>
      <DialogHeader title="Alterar Senha" onClose={hideModal} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={12}>
              <ControlledTextInput
                control={control}
                name="current_password"
                label="Senha Atual"
                type="password"
                errorMessage={errors.current_password?.message?.toString()}
                variant="outlined"
                helperText="Para alterar sua senha, é necessário informar a senha que você está utilizando atualmente."
              />
            </Grid>
            <Grid item xs={12} md={12}>
              <ControlledTextInput
                control={control}
                name="new_password"
                label="Nova Senha"
                type="password"
                errorMessage={errors.new_password?.message?.toString()}
                variant="outlined"
              />
              {level && (
                <Box mt={1}>
                  <PasswordStrengthComponent passwordStreght={level} />
                </Box>
              )}
            </Grid>
            <Grid item xs={12} md={12}>
              <ControlledTextInput
                control={control}
                name="password_confirm"
                label="Confirmação de Senha"
                type="password"
                errorMessage={errors.password_confirm?.message?.toString()}
                variant="outlined"
              />
            </Grid>
          </Grid>
          <DialogActions sx={{ pt: 4, px: 0 }}>
            <Button startIcon={<CloseIcon />} variant="outlined" onClick={hideModal}>
              Cancelar
            </Button>
            <Button startIcon={<CheckIcon />} variant="contained" type="submit" disabled={!isDirty || !isValid}>
              Salvar
            </Button>
          </DialogActions>
        </DialogContent>
      </form>
    </Dialog>
  );
};
